if (!customElements.get('rolloffs-product-tabs')) {
  customElements.define('rolloffs-product-tabs', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.desktop = matchMedia('(min-width: 750px)');
      this.tablist = this.querySelector('[role="tablist"]');
      this.source = this.querySelector('[data-resource-source]');
      this.viewer = this.querySelector('dialog');
      this.build();
      this.modeChange = () => this.setMode();
      this.desktop.addEventListener('change', this.modeChange);
      this.querySelector('[data-close-viewer]').addEventListener('click', () => this.viewer.close());
      this.viewer.addEventListener('click', event => { if (event.target === this.viewer) this.viewer.close(); });
    }
    disconnectedCallback() {
      this.desktop?.removeEventListener('change', this.modeChange);
      this.ready = false;
    }
    label(key) { return this.dataset['label' + key[0].toUpperCase() + key.slice(1)] || key; }
    url(value) {
      try {
        const url = new URL(value, 'https://rolloffs.net/');
        return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
      } catch { return null; }
    }
    fileName(url) {
      try { return decodeURIComponent(new URL(url).pathname.split('/').pop()).replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '); }
      catch { return this.label('document'); }
    }
    build() {
      if (this.classList.contains('is-enhanced')) {
        this.panels = [...this.querySelectorAll(':scope > [data-panel]')];
        this.buttons = [...this.tablist.querySelectorAll('button')];
        this.setMode();
        return;
      }
      const source = this.source;
      source.querySelectorAll('script, style, link, meta, iframe').forEach(node => node.remove());
      const sectionKey = heading => {
        const text = heading.textContent.trim().replace(/:$/, '').toLowerCase();
        return ({details:'details', description:'details', features:'features', feature:'features', specifications:'specifications', specification:'specifications', specs:'specifications', performance:'performance', options:'options', manuals:'manuals', manual:'manuals', brochures:'brochures', brochure:'brochures', drawings:'drawings', drawing:'drawings', 'available colors':'colors', 'available colours':'colors', colors:'colors', colours:'colors', 'get a quote':'quote'})[text];
      };
      const headings = [...source.querySelectorAll('h2,h3')].filter(sectionKey);
      const fragments = new Map();
      const append = (key, fragment) => {
        if (!fragments.has(key)) fragments.set(key, document.createElement('div'));
        fragments.get(key).append(fragment);
      };
      const intro = document.createRange();
      intro.selectNodeContents(source);
      if (headings.length) intro.setEndBefore(headings[0]);
      if (!document.querySelector('rolloffs-equipment-intro[data-product-id="' + this.dataset.productId + '"]')) append('details', intro.cloneContents());
      headings.forEach((heading, index) => {
        const range = document.createRange();
        range.selectNodeContents(source);
        range.setStartAfter(heading);
        if (headings[index + 1]) range.setEndBefore(headings[index + 1]);
        append(sectionKey(heading), range.cloneContents());
      });
      const quote = this.querySelector(':scope > [data-panel="quote"]');
      for (const [key, fragment] of fragments) {
        if (key === 'quote') continue;
        if (!fragment.textContent.trim() && !fragment.querySelector('img,a[href]')) continue;
        const panel = document.createElement('details');
        panel.className = 'ro-resources__panel';
        panel.dataset.panel = key;
        const summary = document.createElement('summary'); summary.textContent = this.label(key);
        const body = document.createElement('div'); body.className = 'ro-resources__body rte';
        const title = document.createElement('h2'); title.textContent = this.label(key);
        body.append(title);
        if (['brochures', 'drawings', 'colors', 'manuals'].includes(key)) this.formatResources(fragment, body, key);
        else { this.fixLinks(fragment); body.append(fragment); }
        panel.append(summary, body);
        this.insertBefore(panel, quote || this.viewer);
      }
      source.remove();
      this.panels = [...this.querySelectorAll(':scope > [data-panel]')];
      this.buttons = this.panels.map((panel, index) => {
        const key = panel.dataset.panel;
        panel.id = this.id + '-panel-' + key;
        const button = document.createElement('button');
        button.type = 'button'; button.role = 'tab';
        button.id = this.id + '-tab-' + key;
        button.textContent = this.label(key);
        button.setAttribute('aria-controls', panel.id);
        button.addEventListener('click', () => this.select(index));
        button.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.buttons.length;
          if (event.key === 'ArrowLeft') next = (index + this.buttons.length - 1) % this.buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.buttons.length - 1;
          if (next !== undefined) { event.preventDefault(); this.select(next); this.buttons[next].focus(); }
        });
        this.tablist.append(button);
        return button;
      });
      this.active = this.querySelector('[data-quote-result]') ? this.panels.indexOf(quote) : 0;
      this.tablist.hidden = !this.panels.length;
      this.classList.add('is-enhanced');
      this.setMode();
    }
    fixLinks(root) {
      root.querySelectorAll('a[href]').forEach(anchor => {
        const url = this.url(anchor.getAttribute('href'));
        if (url) anchor.href = url; else anchor.removeAttribute('href');
      });
      root.querySelectorAll('img').forEach(img => {
        const url = this.url(img.getAttribute('src'));
        if (url) img.src = url;
        img.loading = 'lazy';
      });
    }
    formatResources(fragment, body, key) {
      this.fixLinks(fragment);
      const documents = document.createElement('div'); documents.className = 'ro-resources__documents';
      const grid = document.createElement('div'); grid.className = 'ro-resources__grid';
      const seen = new Set();
      fragment.querySelectorAll('a[href]').forEach(anchor => {
        if (!/\.pdf(?:[?#]|$)/i.test(anchor.href) || seen.has(anchor.href)) return;
        seen.add(anchor.href);
        const row = document.createElement('a'); row.className = 'ro-resources__document';
        row.href = anchor.href; row.target = '_blank'; row.rel = 'noopener';
        const badge = document.createElement('span'); badge.textContent = 'PDF';
        const text = document.createElement('span');
        const title = document.createElement('strong'); title.textContent = anchor.textContent.trim() || this.fileName(anchor.href);
        const action = document.createElement('small'); action.textContent = this.label('view');
        text.append(title, action); row.append(badge, text); documents.append(row);
      });
      fragment.querySelectorAll('img').forEach(img => {
        const src = this.url(img.getAttribute('src'));
        if (!src || seen.has(src)) return;
        const parent = img.closest('a[href]');
        if (['brochures', 'manuals'].includes(key) && parent && /\.pdf(?:[?#]|$)/i.test(parent.href)) return;
        seen.add(src);
        const title = img.alt.trim() || this.fileName(src);
        const figure = document.createElement('figure'); figure.className = 'ro-resources__figure';
        const button = document.createElement('button'); button.type = 'button'; button.className = 'ro-resources__image';
        button.setAttribute('aria-label', this.label('view') + ': ' + title);
        const preview = document.createElement('img'); preview.src = src; preview.alt = title;
        preview.loading = 'lazy'; preview.decoding = 'async'; preview.width = 480; preview.height = 360;
        button.append(preview);
        const caption = document.createElement('figcaption'); caption.textContent = title;
        button.addEventListener('click', () => {
          const large = this.viewer.querySelector('img'); large.src = src; large.alt = title;
          this.viewer.querySelector('a').href = src;
          this.viewer.showModal();
        });
        figure.append(button, caption); grid.append(figure);
      });
      if (documents.children.length) body.append(documents);
      if (grid.children.length) body.append(grid);
      if (!documents.children.length && !grid.children.length) body.append(fragment);
    }
    select(index) {
      this.active = index;
      this.panels.forEach((panel, i) => {
        panel.toggleAttribute('data-active', i === index);
        if (this.desktop.matches) panel.open = true;
        this.buttons[i].setAttribute('aria-selected', String(i === index));
        this.buttons[i].tabIndex = i === index ? 0 : -1;
      });
    }
    setMode() {
      this.panels.forEach((panel, index) => {
        if (this.desktop.matches) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', this.buttons[index].id);
          panel.tabIndex = 0;
        } else {
          panel.removeAttribute('role'); panel.removeAttribute('aria-labelledby'); panel.removeAttribute('tabindex');
          panel.open = index === this.active;
        }
      });
      this.select(this.active);
    }
  });
}


